import { useState } from 'react';
import AccountActionSuccess from './AccountActionSuccess';
import Text from '@/components/Text';
import RulaButton from '@pathccm/rula-ui/button';
import { RulaTextField } from '@pathccm/rula-ui/form';
import RulaLink from '@pathccm/rula-ui/link';
import fetch from '@/utils/fetch';
import { Trigger } from '@/utils/types';
import { useNavigate } from 'react-router-dom';
import { usePatientAccountDetails } from '@/utils/use-personal-details';

interface AccountTransferProps {
  trackAccountAction: (action: {
    name: string;
    cta_type?: string;
    metadata?: any;
    trigger?: Trigger;
  }) => void;
}

const AccountTransfer = ({ trackAccountAction }: AccountTransferProps) => {
  const [view, setView] = useState<'submit-email' | 'submit-email-success'>(
    'submit-email',
  );
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [patientEmail, setPatientEmail] = useState('');
  const [confirmedPatientEmail, setConfirmedPatientEmail] = useState('');
  const navigate = useNavigate();
  const { patientName, patientUuid } = usePatientAccountDetails();

  const handleDeclareGuardianshipClick = () => {
    trackAccountAction({
      name: 'accountTransferPage_CTAClicked',
      cta_type: 'medical_guardianship',
    });

    navigate(`/declare-guardian/${patientUuid}`);
  };
  const isValidEmailFormat = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(confirmedPatientEmail);
  };

  const validate = () => {
    if (!isValidEmailFormat()) {
      return 'Invalid email address';
    }
    if (patientEmail !== confirmedPatientEmail) {
      return 'Emails do not match';
    }
  };

  const handleSubmit = async () => {
    if (validate()) {
      return;
    } else {
      setFormError(null);
      setSubmitting(true);
      trackAccountAction({
        name: 'accountTransferPage_CTAClicked',
        cta_type: 'submit_transfer',
      });
      try {
        await fetch.json('/api/set_graduation_email', {
          method: 'POST',
          body: {
            // we should always have this, but it is from a query param
            patient_uuid: patientUuid!,
            email: confirmedPatientEmail,
          },
        });
        setView('submit-email-success');
      } catch (e) {
        trackAccountAction({
          name: 'failure',
          metadata: { account_action: 'account_transfer', failure_reason: e },
        });
        console.error(e);
        setFormError('Failed to submit email. Please try again.');
      }
      setSubmitting(false);
    }
  };

  if (view === 'submit-email-success') {
    return (
      <AccountActionSuccess
        copy={`${patientName} (${confirmedPatientEmail}) will receive an email shortly!`}
        refreshAccounts={() => {
          window.location.href = '/';
        }}
        trackAccountAction={trackAccountAction}
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-8 md:flex-row md:gap-24">
      <div className="flex flex-col gap-6 w-full max-w-[305px] justify-center items-center text-center">
        <Text.H1>Account transfer</Text.H1>
        <Text.P>
          Now that {patientName} is turning 18, they will be responsible for
          managing their care through Rula. To avoid any lapse in their care,
          please enter the email they will use to access their account.
        </Text.P>
        <Text.P>
          On their 18th birthday, they’ll receive instructions for signing into
          their account, and future communications from Rula will be sent to
          their email address.
        </Text.P>
      </div>

      <div className="flex flex-col gap-7 w-full max-w-[305px] justify-center items-center text-center">
        <div className="flex flex-col gap-6 w-full">
          <RulaTextField
            label={`${patientName}'s email`}
            className="self-start w-full text-left"
            onChange={(value) => setPatientEmail(value)}
          />
          <RulaTextField
            label={`Confirm ${patientName}'s email`}
            className="self-start w-full text-left"
            onChange={(value) => setConfirmedPatientEmail(value)}
            validate={validate}
          />
          {formError && <Text.P className="text-red-50">{formError}</Text.P>}
          <RulaButton
            onPress={() => handleSubmit()}
            size="Large"
            className="w-full"
            style="Primary"
            isDisabled={!confirmedPatientEmail || !patientEmail}
            loading={submitting}
          >
            Submit
          </RulaButton>
        </div>

        <div className="flex flex-col gap-7 w-full">
          <div className="flex justify-center items-center gap-1">
            <div className="flex-grow border-b-1" />
            <Text.Small className="text-gray-400">
              Or declare medical guardianship
            </Text.Small>
            <div className="flex-grow border-b-1" />
          </div>

          <Text.P>
            <RulaLink
              onPress={handleDeclareGuardianshipClick}
              className="text-primary"
            >
              Click here
            </RulaLink>{' '}
            for medical guardianship
          </Text.P>
        </div>
      </div>
    </div>
  );
};
export default AccountTransfer;
